import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  RefreshCw, ArrowLeft, ArrowUpCircle, ArrowDownCircle, 
  Package, ShoppingCart, Clock, XCircle 
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/libritos';

const MOVEMENT_TYPES = {
  ingreso: { label: "Ingreso", color: "bg-green-100 text-green-700", icon: ArrowUpCircle },
  reserva: { label: "Reserva", color: "bg-blue-100 text-blue-700", icon: ShoppingCart },
  venta_presencial: { label: "Venta", color: "bg-violet-100 text-violet-700", icon: Package },
  cancelacion: { label: "Cancelación", color: "bg-amber-100 text-amber-700", icon: RefreshCw },
  vencimiento: { label: "Vencimiento", color: "bg-red-100 text-red-700", icon: Clock },
  ajuste: { label: "Ajuste", color: "bg-gray-100 text-gray-700", icon: RefreshCw },
};

export default function AdminLibritosMovimientosPage() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMovements();
  }, []);

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stock-movements`);
      // Sort by date descending
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp)
      );
      setMovements(sorted);
    } catch (error) {
      toast.error("Error al cargar movimientos");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMovementBadge = (type) => {
    const config = MOVEMENT_TYPES[type] || MOVEMENT_TYPES.ajuste;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/libritos">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Movimientos de Stock</h1>
          <p className="text-gray-500">Historial de cambios en el inventario</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Movimientos ({movements.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Libro</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-center">Cambio</TableHead>
                <TableHead className="text-center">Stock Antes</TableHead>
                <TableHead className="text-center">Stock Después</TableHead>
                <TableHead>Referencia</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movements.map((mov, index) => (
                <TableRow key={mov.id || index}>
                  <TableCell className="text-sm">{formatDate(mov.created_at || mov.timestamp)}</TableCell>
                  <TableCell className="font-medium">{mov.book_title}</TableCell>
                  <TableCell>{getMovementBadge(mov.movement_type)}</TableCell>
                  <TableCell className="text-center">
                    <span className={`font-bold ${mov.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {mov.quantity_change > 0 ? '+' : ''}{mov.quantity_change}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-gray-500">{mov.stock_before}</TableCell>
                  <TableCell className="text-center font-semibold">{mov.stock_after}</TableCell>
                  <TableCell className="font-mono text-xs text-gray-500">
                    {mov.reference_id || '-'}
                  </TableCell>
                </TableRow>
              ))}
              {movements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No hay movimientos registrados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
